import { useMemo } from "react";
import { createTheme } from "@mui/material";
import { alpha } from "@mui/system/colorManipulator";
import { deepmerge } from "@climatepartner/utils";
import { fontFaceIBMPlexSans } from "./fontFace";
export const generateCpTheme = ({ mode, theme } = {}) => {
    const hasMatchMedia = typeof window !== "undefined" && window.matchMedia;
    const prefersDarkMode = hasMatchMedia ? window.matchMedia("(prefers-color-scheme: dark)").matches : false;
    const isDarkMode = ["light", "dark"].includes(String(mode)) ? mode !== "light" : prefersDarkMode;
    const textColor = isDarkMode ? "#FFFFFF" : "#000000";
    const colourPalette = {
        mode: isDarkMode ? "dark" : "light",
        primary: {
            light: "#4182DC",
            main: "#003C91",
            dark: "#001862",
            contrastText: "#FFFFFF",
        },
        secondary: {
            light: "#64CDF0",
            main: "#1EBEE6",
            dark: "#008EB4",
            contrastText: "#FFFFFF",
        },
        error: {
            light: "#E3542D",
            main: "#AA1E00",
            dark: "#780000",
            contrastText: "#FFFFFF",
        },
        info: {
            light: "#5F9CE7",
            main: "#1E6EB4",
            dark: "#004484",
            contrastText: "#FFFFFF",
        },
        warning: {
            light: "#E6AF7D",
            main: "#E69100",
            dark: "#AE6300",
            contrastText: "#FFFFFF",
        },
        success: {
            light: "#DAD646",
            main: "#95B733",
            dark: "#5A641E ",
            contrastText: "#FFFFFF",
        },
        background: {
            default: isDarkMode ? "#121212" : "#e5eaf1",
        },
        text: {
            primary: "#212121",
            secondary: "#5B5B5B",
            disabled: "#8D8D8D",
        },
        action: {
            active: alpha(textColor, 0.54),
            activatedOpacity: 0.54,
            hover: alpha(textColor, 0.04),
            hoverOpacity: 0.04,
            selected: alpha(textColor, 0.08),
            selectedOpacity: 0.08,
            disabled: alpha(textColor, 0.26),
            disabledOpacity: 0.26,
            disabledBackground: alpha(textColor, 0.12),
            focus: alpha(textColor, 0.12),
            focusOpacity: 0.12,
        },
        divider: alpha("#000000", 0.12),
    };
    const cpTheme = createTheme(deepmerge({
        breakpoints: {
            values: {
                xs: 0,
                mobile: 0,
                tablet: 640,
                laptop: 1024,
                desktop: 1440,
                sm: 1104,
                md: 1200,
                lg: 1536,
                xl: 1920,
            },
        },
        palette: colourPalette,
        typography: {
            h1: {
                fontSize: "1.5rem",
                fontWeight: 600,
                lineHeight: 1.3,
                letterSpacing: 1,
            },
            h2: {
                fontSize: "1.25rem",
                fontWeight: 600,
                lineHeight: 1.2,
                letterSpacing: 0.15,
            },
            h3: {
                fontSize: "1.125rem",
                fontWeight: 600,
                lineHeight: 1.3,
                letterSpacing: 0.3,
            },
            h4: {
                fontSize: "1rem",
                fontWeight: 500,
                lineHeight: 1.3,
                letterSpacing: 0.25,
                textTransform: "uppercase",
            },
            h5: {
                fontSize: "1rem",
                fontWeight: 500,
                lineHeight: 1.2,
                letterSpacing: 0.1,
            },
            h6: {
                fontSize: "0.875rem",
                fontWeight: 500,
                lineHeight: 1.4,
                letterSpacing: 0.15,
            },
            subtitle1: {
                fontSize: "1rem",
                fontWeight: 400,
                lineHeight: 1.6,
                letterSpacing: 0.15,
            },
            subtitle2: {
                fontSize: "0.875rem",
                fontWeight: 400,
                lineHeight: 1.5,
                letterSpacing: 0.15,
            },
            body1: {
                fontSize: "0.875rem",
                fontWeight: 400,
                lineHeight: 1.5,
                letterSpacing: 0.15,
            },
            body2: {
                fontSize: "0.75rem",
                fontWeight: 400,
                lineHeight: 1.4,
                letterSpacing: 0.15,
            },
            button: {
                textTransform: "none",
            },
            caption: {
                fontSize: "0.75rem",
                fontWeight: 400,
                lineHeight: 1.5,
                letterSpacing: 0.4,
            },
            overline: {
                fontSize: "0.875rem",
                fontWeight: 400,
                lineHeight: 1.4,
                letterSpacing: 0.15,
                textTransform: "uppercase",
            },
            fontFamily: ["IBM Plex Sans", "sans-serif"].join(","),
        },
        components: {
            MuiButton: {
                defaultProps: {
                    variant: "contained",
                },
                styleOverrides: {
                    root: {
                        textTransform: "uppercase",
                    },
                },
            },
            MuiCssBaseline: {
                styleOverrides: {
                    fallback: [...fontFaceIBMPlexSans],
                    html: {
                        minHeight: "100%",
                    },
                    body: {
                        minHeight: "100vh",
                        height: "100%",
                        display: "flex",
                    },
                    // todo: replace this with AppWrapper component in old apps
                    "#root": {
                        display: "flex",
                        flex: 1,
                        flexFlow: "column nowrap",
                        justifyContent: "stretch",
                        alignItems: "stretch",
                        width: "100%",
                    },
                },
            },
            MuiTooltip: {
                defaultProps: {
                    arrow: true,
                },
            },
            MuiDataGrid: {
                styleOverrides: {
                    root: {
                        fontSize: "0.875rem",
                    },
                },
            },
            MuiTextField: {
                defaultProps: {
                    fullWidth: true,
                    size: "small",
                },
            },
            MuiSelect: {
                defaultProps: {
                    size: "small",
                },
            },
            MuiFormControl: {
                defaultProps: {
                    size: "small",
                },
            },
        },
    }, theme ?? {}));
    if (process.env.NODE_ENV === "development") {
        console.groupCollapsed("Theme");
        console.info(theme);
        console.groupEnd();
    }
    return cpTheme;
};
export function useCpTheme({ mode, theme } = {}) {
    return useMemo(() => generateCpTheme({ mode, theme }), [mode, theme]);
}
//# sourceMappingURL=theme.js.map